import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import { DataGrid } from "@mui/x-data-grid";
import {
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  IconButton,
  Modal,
  Box,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useStudent } from "./hooks";
import { getCourseAssignments } from "../../store/study/studyBaseApi";
import { student as studentConstant } from "./constants";
import { getAllWithoutPage } from "../../store/course/courseBaseApi";
import { Eye, Headphones, Minus, X } from "@phosphor-icons/react";
import Loader from "../../components/Loader";
import PreviewAssessment from "./PreviewAssesment";
import AudioToPlay from "./AudioToPlay";

// timeTaken is stored in whole seconds.
const formatDuration = (value) => {
  const total = Number(value);
  if (value === null || value === "" || !Number.isFinite(total)) return "-";
  const minutes = Math.floor(total / 60);
  const seconds = Math.round(total % 60);
  return minutes ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

export const StudentActivity = ({ studentId, passedOnly = false }) => {
  const {
    loadCurrent,
    current,
    loadActivity,
    activities,
    loadAssessmentSummary,
    loadProgressSummary,
    loadAssignmentProgress,
    assessmentSummary,
    progressSummary,
    AssignmentProgressSummary,
    loading,
    loadAssessmentResult,
    assessmentResult,
  } = useStudent();
  const theme = useTheme();
  const id = studentId;
  const navigate = useNavigate();
  const [studentDetails, setStudentDetails] = useState();
  const [courseList, setCourseList] = useState([]);
  const [course, setCourse] = useState();
  // const [courseId, setCourseId] = useState();
  const [activityList, setActivityList] = useState([]);
  const [progressList, setProgressList] = useState([]);
  const [assessmentList, setAsseessmentList] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState();
  const [result, setResult] = useState();
  const [resultLoading, setResultLoading] = useState(false);
  const [openAudioModal, setOpenAudioModal] = useState(false);
  const [speakAloud, setSpeakAloud] = useState();

  // const [params,setParems] = useState()

  const formatDate = (dateString) => {
    return moment(dateString).format("DD-MM-YYYY h:mm A");
  };

  const activityColumns = [
    { field: "type", headerName: "Type", flex: 1, sortable: true },
    {
      field: "startTime",
      headerName: "Start Date",
      flex: 1,
      sortable: true,
      renderCell: (params) => <div>{formatDate(params.value)}</div>,
    },
    {
      field: "endTime",
      headerName: "End Date",
      flex: 1,
      renderCell: (params) => <div>{formatDate(params.value)}</div>,
    },

    { field: "chapterName", headerName: "Session", flex: 1, sortable: true },
    { field: "sectionName", headerName: "Chapter", flex: 1, sortable: true },
    {
      field: "audio",
      headerName: "Audio",
      flex: 1,
      renderCell: (params) => (
        <div>
          {params.row.type === "SPEAK_ALOUD" ||
          params.row.type === "INCORRECT_SPEAK_ALOUD" ||
          params.row.type === "STORY_READING" ? (
            <IconButton onClick={() => handleOpenAudio(params.row)}>
              <Headphones style={styles.Icon} />
            </IconButton>
          ) : (
            <IconButton disabled>
              <Minus />
            </IconButton>
          )}
        </div>
      ),
    },
  ];

  const progressColumns = [
    {
      field: "startTime",
      headerName: "Start Date",
      flex: 1,
      sortable: true,
      renderCell: (params) => <div>{formatDate(params.value)}</div>,
    },
    {
      field: "endTime",
      headerName: "End Date",
      flex: 1,
      sortable: true,
      renderCell: (params) => <div>{formatDate(params.value)}</div>,
    },
    {
      field: "gradeAchieved",
      headerName: "Grade Achieved",
      flex: 1,
      sortable: true,
    },
    { field: "chapterName", headerName: "Session", flex: 1, sortable: true },
    { field: "sectionName", headerName: "Chapter", flex: 1, sortable: true },
  ];

  const assessmentColumns = [
    {
      field: "assessmentName",
      headerName: "Assessment",
      flex: 1,
      sortable: true,
    },
    {
      field: "attendedOn",
      headerName: "Attended Date",
      flex: 1,
      sortable: true,
      renderCell: (params) => <div>{formatDate(params.value)}</div>,
    },
    { field: "gradeAchieved", headerName: "Grade Achieved", flex: 1 },
    { field: "scored", headerName: "Scored Marks", flex: 1 },
    { field: "chapterName", headerName: "Session", flex: 1, sortable: true },
    { field: "sectionName", headerName: "Chapter", flex: 1, sortable: true },
    {
      field: "timeTaken",
      headerName: "Time Taken",
      flex: 1,
      sortable: true,
      renderCell: (params) => <div>{formatDuration(params.value)}</div>,
    },
    { field: "total", headerName: "Total marks", flex: 1, sortable: true },
    {
      field: "actions",
      headerName: "Preview",
      width: 150,
      renderCell: (params) => (
        <strong>
          <IconButton onClick={() => handlePreview(params.row)}>
            <Eye
              size={22}
              sx={{
                color: "var(--mui-palette-primary-button)",
                fontSize: "1.2rem",
              }}
            />
          </IconButton>
        </strong>
      ),
    },
  ];

  const handleOpenAudio = (speakAloud) => {
    setSpeakAloud(speakAloud);
    setOpenAudioModal(true);
  };

  const handleCloseAudio = () => {
    setSpeakAloud();
    setOpenAudioModal(false);
  };

  const handlePreview = (assessment) => {
    setSelectedAssessment(assessment);
    setResult(null);
    setResultLoading(true);
    setOpenPreview(true);
    loadAssessmentResult({ assessmentId: assessment.assessmentId, id });
  };

  const handleClose = () => {
    setSelectedAssessment();
    setResult(null);
    setOpenPreview(false);
  };
  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    let studentCourseDetails = await getCourseAssignments({ userId: id });
    setCourseList(studentCourseDetails?.data?.courses);
    console.log(studentCourseDetails?.data?.courses);
    if (studentCourseDetails?.data?.courses) {
      setCourse(studentCourseDetails?.data?.courses[0]);
    }
    // setCourseId(studentCourseDetails?.data?.courses[0]?.courseId)
    let courses = await getAllWithoutPage();
    // setCourseList(courses?.data?.data)
  };

  useEffect(() => {
    if (id) {
      loadCurrent({ id });
    }
  }, [id]);

  useEffect(() => {
    setStudentDetails(current);
  }, [current]);

  useEffect(() => {
    let tempActivity = activities
      .map((data, index) => {
        let acti = {
          ...data,
          id: index,
          // startTime:dateFormate(data?.startTime)
        };
        return acti;
      })
      .sort((a, b) => new Date(b.endTime) - new Date(a.endTime));

    let tempProgress = progressSummary.map((data, index) => {
      let acti = { ...data, id: index };
      return acti;
    });
    let tempAssessment = assessmentSummary.map((data, index) => {
      let acti = { ...data, id: index };
      return acti;
    });
    setActivityList(tempActivity);
    setProgressList(tempProgress);
    setAsseessmentList(tempAssessment);

    // setStudentDetails(current)
  }, [activities, progressSummary, assessmentSummary]);

  useEffect(() => {
    if (course) {
      const params = {
        courseId: course?.courseId,
        id: id,
      };
      loadActivity(params);
      // /my-activity (self view) fetches only passed assessment attempts.
      loadAssessmentSummary({ ...params, passedOnly });
      loadProgressSummary(params);
      // loadAssignmentProgress(params);
    }
  }, [course]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleChange = (e) => {
    const course = e.target.value;
    setCourse(course);
    // setCourseId(course)
  };

  useEffect(() => {
    setResult(assessmentResult);
    setResultLoading(false);
  }, [assessmentResult]);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <Grid
            sx={styles.gridContainer}
            container
            spacing={2}
            alignItems="center"
            justifyContent="flex-end"
          >
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <InputLabel style={styles.inputLabel}>Course</InputLabel>
              <FormControl fullWidth>
                <Select
                  name="courseId"
                  value={course || ""}
                  onChange={(e) => handleChange(e)}
                  placeholder="IR Level"
                  sx={{ height: "47px" }}
                >
                  {courseList &&
                    courseList.map((item, index) => {
                      return (
                        <MenuItem key={index} value={item}>
                          {item?.courseName}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid
            sx={styles.gridContainer}
            container
            spacing={2}
            alignItems="center"
          >
            <Grid item xs={12} sx={{ marginBottom: "50px" }}>
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
              >
                {studentConstant.ACTIVITY_TABS.map((item, index) => (
                  <Tab label={item} key={index} />
                ))}
              </Tabs>
            </Grid>
            {selectedTab === 0 && (
              <div style={{ minHeight: 500, width: "100%", overflow: "auto" }}>
                <DataGrid
                  rows={activityList}
                  columns={activityColumns}
                  disableRowSelectionOnClick
                  autoHeight
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 10,
                      },
                    },
                  }}
                  has
                  context
                  menu
                  sx={{
                    backgroundColor: "var(--mui-palette-common-white)",
                    "--DataGrid-overlayHeight": "300px",
                    [theme.breakpoints.down("sm")]: {
                      width: "900px",
                    },
                    "& .css-yrdy0g-MuiDataGrid-columnHeaderRow": {
                      background: "var(--mui-palette-primary-blue) !important",
                      color: "var(--mui-palette-action-active)",
                    },
                    "& .MuiDataGrid-columnHeader": {
                      background: "var(--mui-palette-primary-blue) !important",
                      color: "var(--mui-palette-common-white)",
                      "&:focus": {
                        outline: "none",
                      },
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontSize: "14px",
                      fontWeight: "bold",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "var(--mui-palette-common-white)",
                    },
                    "& .MuiDataGrid-cell:focus": {
                      outline: "none",
                      border: "none",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "black", // This changes the pagination arrow color
                    },
                  }}
                />
              </div>
            )}
            {selectedTab === 1 && (
              <div style={{ minHeight: 500, width: "100%", overflow: "auto" }}>
                <DataGrid
                  rows={progressList}
                  columns={progressColumns}
                  disableRowSelectionOnClick
                  autoHeight
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 10,
                      },
                    },
                  }}
                  has
                  context
                  menu
                  sx={{
                    backgroundColor: "var(--mui-palette-common-white)",
                    "--DataGrid-overlayHeight": "300px",
                    [theme.breakpoints.down("sm")]: {
                      width: "900px",
                    },
                    "& .css-yrdy0g-MuiDataGrid-columnHeaderRow": {
                      background: "var(--mui-palette-primary-blue) !important",
                      color: "var(--mui-palette-action-active)",
                    },
                    "& .MuiDataGrid-columnHeader": {
                      background: "var(--mui-palette-primary-blue) !important",
                      color: "var(--mui-palette-common-white)",
                      "&:focus": {
                        outline: "none",
                      },
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontSize: "14px",
                      fontWeight: "bold",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "var(--mui-palette-common-white)",
                    },
                    "& .MuiDataGrid-cell:focus": {
                      outline: "none",
                      border: "none",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "black", // This changes the pagination arrow color
                    },
                  }}
                />
              </div>
            )}
            {selectedTab === 2 && (
              <div style={{ minHeight: 500, width: "100%", overflow: "auto" }}>
                <DataGrid
                  rows={assessmentList}
                  columns={assessmentColumns}
                  disableRowSelectionOnClick
                  autoHeight
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 10,
                      },
                    },
                  }}
                  has
                  context
                  menu
                  sx={{
                    backgroundColor: "var(--mui-palette-common-white)",
                    "--DataGrid-overlayHeight": "300px",
                    [theme.breakpoints.down("sm")]: {
                      width: "900px",
                    },
                    "& .css-yrdy0g-MuiDataGrid-columnHeaderRow": {
                      background: "var(--mui-palette-primary-blue) !important",
                      color: "var(--mui-palette-action-active)",
                    },
                    "& .MuiDataGrid-columnHeader": {
                      background: "var(--mui-palette-primary-blue) !important",
                      color: "var(--mui-palette-common-white)",
                      "&:focus": {
                        outline: "none",
                      },
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontSize: "14px",
                      fontWeight: "bold",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "var(--mui-palette-common-white)",
                    },
                    "& .MuiDataGrid-cell:focus": {
                      outline: "none",
                      border: "none",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "black", // This changes the pagination arrow color
                    },
                  }}
                />
              </div>
            )}
            <Modal open={openPreview}>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "80%",
                  height: "80%",
                  bgcolor: "background.paper",
                  boxShadow: 24,
                  borderRadius: "10px",
                  p: 4,
                  overflow: "auto",
                }}
              >
                <IconButton
                  onClick={handleClose}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                  }}
                >
                  <X />
                </IconButton>
                {resultLoading ? (
                  <Loader />
                ) : result ? (
                  <PreviewAssessment
                    student={current}
                    course={course}
                    result={result}
                    selectedAssessment={selectedAssessment}
                  />
                ) : null}
              </Box>
            </Modal>
            <Modal open={openAudioModal}>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "50%",
                  height: "50%",
                  bgcolor: "background.paper",
                  boxShadow: 24,
                  borderRadius: "10px",
                  p: 4,
                  overflow: "auto",
                }}
              >
                <IconButton
                  onClick={handleCloseAudio}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                  }}
                >
                  <X />
                </IconButton>
                <AudioToPlay speakAloud={speakAloud} />
              </Box>
            </Modal>
          </Grid>
        </>
      )}
    </>
  );
};

const styles = {
  label: {
    color: "var(--mui-palette-common-black)",
    fontWeight: "bold",
  },
  inputLabel: {
    color: "var(--mui-palette-primary-darkGrey)",
    marginBottom: "5px",
    fontWeight: "600",
    fontSize: "14px",
  },
  gridContainer: {
    mt: 2,
    display: "flex",
  },
  suggestionBox: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    marginTop: "10px",
  },
  suggestionItem: {
    padding: "10px",
    border: "1px solid #ccc",
    borderRadius: "2px",
    cursor: "pointer",
    boxShadow: "0 4px 8px var(--mui-palette-secondary-boxShadow1)",
    backgroundColor: "var(--mui-palette-gray-lightGrey300)",
  },
  uploadBtn: {
    width: "50px",
    height: "50px",
    borderRadius: "5px",
  },
  hiddenInput: {
    display: "none",
  },
  Icon: {
    cursor: "pointer",
    color: "#635bff",
  },
};
